/**
 * Responsibility: Manage PWA lifecycle, installation prompts, and update notifications.
 * Scope: Unified PWA bridge and manager; interacts with Service Worker and Dispatcher.
 */
import { dispatcher } from './app-states'
import { TOPICS } from "@anyquest/shared-core"

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const EVT_INSTALLABLE = 'am2:pwa-installable'
const EVT_INSTALLED = 'am2:pwa-installed'
const EVT_UPDATE = 'am2:pwa-update-ready'

let initialized = false
let deferredPrompt: BeforeInstallPromptEvent | null = null
let registration: ServiceWorkerRegistration | null = null
let waitingWorker: ServiceWorker | null = null
let refreshing = false

function emit(eventName: string): void {
  window.dispatchEvent(new CustomEvent(eventName))
}

function setWaitingWorker(worker: ServiceWorker | null): void {
  waitingWorker = worker
  if (waitingWorker) {
    emit(EVT_UPDATE)
    dispatcher.trigger('pwa', TOPICS.system.pwaUpdateReady, {})
    dispatcher.trigger('pwa', TOPICS.ui.notify, { message: 'Update ready. Apply from profile menu.', level: 'info' })
  }
}

export function initPwa(): void {
  if (initialized || typeof window === 'undefined') return
  initialized = true

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    emit(EVT_INSTALLABLE)
    dispatcher.trigger('pwa', TOPICS.system.pwaInstallable, {})
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    emit(EVT_INSTALLED)
    dispatcher.trigger('pwa', TOPICS.system.pwaInstalled, {})
    dispatcher.trigger('pwa', TOPICS.ui.notify, { message: 'PWA installed.', level: 'info' })
  })

  if (!('serviceWorker' in navigator)) return

  window.addEventListener('load', async () => {
    try {
      registration = await navigator.serviceWorker.register('/sw.js')
      if (registration.waiting) setWaitingWorker(registration.waiting)
      registration.addEventListener('updatefound', () => {
        const installing = registration?.installing
        if (!installing) return
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(installing)
          }
        })
      })
    } catch (error) {
      console.warn('[PWA] service worker registration failed', error)
    }
  })

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return
    refreshing = true
    window.location.reload()
  })
}

export function isPwaInstallPromptAvailable(): boolean {
  return !!deferredPrompt
}

export async function requestPwaInstall(): Promise<boolean> {
  if (!deferredPrompt) return false
  const prompt = deferredPrompt
  deferredPrompt = null
  await prompt.prompt()
  const choice = await prompt.userChoice
  return choice.outcome === 'accepted'
}

export function isPwaUpdateAvailable(): boolean {
  return !!waitingWorker
}

export function applyPwaUpdate(): boolean {
  if (!waitingWorker) return false
  waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  return true
}
